import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { HAConfig } from 'src/app/core/models/HAConfig';
import { HaService } from 'src/app/core/services/ha.service';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthenticationService } from 'src/app/core/services/authentication.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HAConfigResponse } from 'src/app/core/models/HAConfigResponse';
import { HAHostInfo } from 'src/app/core/models/HAHostInfo';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { UpdateHostIpComponent } from './update-host-ip.component';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { CoreModule } from 'src/app/core/core.module'; 
import { LinkedServerInfo } from 'src/app/core/models/dbserverinfo';
import { finalize } from 'rxjs';
import { ResetConfirmationDialogComponent } from './reset-confirmation-dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MakePrimaryConfirmationDialogComponent } from './make-primary-confirmation-dialog';

interface DatabaseOption{
  value: string;
  label: string;
}

interface SyncStatus {
  HAEnabled: boolean;

  IsDBSyncRunning: boolean;
  DBStatus: string;
  DBMessage: string;
  DBLastSyncTime: Date;

  IsFSSyncRunning: boolean;
  FSStatus: string;
  FSMessage: string;
  FSLastSyncTime: Date;

  IsJobsSyncRunning: boolean | null;
  JobsStatus?: string | null;
  JobsSyncMessage?: string | null;
  JobsSyncLastSyncTime?: Date | null;
}

interface SyncResultHA {
  status: string;
  processedFromCurrent: number;
  failedEntries: number;
  remainingEntries: number;
  executionTimeSeconds: number;
  completedAt: Date;
}

@Component({
  selector: 'app-ha-config',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    MatDividerModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatExpansionModule,
    CommonModule,
    MatSlideToggleModule,
    NgxSpinnerModule,
    NgbTooltipModule,
    CoreModule
],
  templateUrl: './ha-config.component.html',
  styleUrl: './ha-config.component.scss'
})
export class HaConfigComponent implements OnInit {

  haEnabled: boolean = false;
  dbtypeEnabled: boolean = true;
  showHostsInfo: boolean = false;
  haConfig !: FormGroup;
  databaseTypes: DatabaseOption[] = [
    { value: 'MSSQL_EXPRESS', label: 'MSSQL EXPRESS' },
    { value: 'MSSQL_STANDARD', label: 'MSSQL STANDARD' },
    { value: 'MSSQL_ENTERPRISE', label: 'MSSQL ENTERPRISE' },
    { value: 'MYSQL', label: 'MYSQL' }
  ];
  primaryHost?: HAHostInfo;
  secondaryHost?: HAHostInfo;
  currentHostIp: string = '';
  isChecking = false;
  checkingHost = '';
  syncStatus?: SyncStatus = null;
  primaryToSecondaryLinking?: boolean = null;
  secondaryToPrimaryLinking?: boolean = null;
  linkedServerInfo: LinkedServerInfo[] = [];

  isSaving = false;

  isStartingDBSync = false;
  isStopingDBSync = false;
  isManualDBSync = false;

  isStartingFSSync = false;
  isStopingFSSync = false;
  isManualFSSync = false;

  isStartingJobsSync = false;
  isStopingJobsSync = false;
  isManualJobsSync = false;

  isConfiguringPrimary = false;
  isConfiguringSecondary = false;

  showAdvancedOptions: boolean = false;
  isResetting: boolean = false;
  isMakingPrimary: boolean = false;

  constructor(
    public form: FormBuilder,
    private haService: HaService,
    private snackBar: MatSnackBar,
    private authenticationService: AuthenticationService,
    private spinner: NgxSpinnerService,
    private dialog: MatDialog,
  ){
    this.initForm(new HAConfig());
  }

  ngOnInit() {
    this.spinner.show("haConfigSpinner");
    this.haService.getHAConfig().subscribe({
      next: (data: any) => {
        
        if (data) {
          const conf = data as HAConfig;  
          this.initForm(conf);
          this.showHostsInfo = conf.useHa;
          this.currentHostIp = this.haService.currentHostIp;
          this.loadHostsInformation();
          this.loadSyncStatus();
          // this.loadLinkedServersInfo();
          // if (conf.useHa) {
          //   this.checkDBLinking();
          // }
        } else {
          const conf = new HAConfig();
          this.initForm(conf);
        }
        this.spinner.hide("haConfigSpinner");
      },
      error: (error) => {
        console.error("Error fetching HA config: ", error);
        const conf = new HAConfig();
        this.initForm(conf);
        this.spinner.hide("haConfigSpinner");
        
        this.showError(`Failed to load HA Configuration: ${this.getErrorMessage(error)}`);
      }
    });
    this.haConfig.valueChanges.subscribe(() => {});
  }

  // Helper Functions
  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      panelClass: ['snack-bar-success']
    });
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      panelClass: ['snack-bar-error']
    });
  }

  private showWarning(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 7000,
      panelClass: ['snack-bar-warning']
    });
  }

  private getErrorMessage(error: any): string {
    if (error?.error?.Message) return error.error.Message;
    if (error?.error?.message) return error.error.message;
    if (error?.error && typeof error.error === 'string') return error.error;
    if (error?.statusText) return error.statusText;
    if (error?.message) return error.message;
    return 'An unexpected error occurred';
  }

  // API Methods with Individual Response Handling

  onSubmit() {
    if (this.haConfig.valid) {
      this.isSaving = true;
      
      this.haService.updateHAConfig(this.haConfig.value, this.authenticationService.currentUserValue.username).subscribe({
        next: (response: any) => {
          
          // HAConfigUpdateDTO response handling
          if (response?.Message === 'Succeeded' || response?.message === 'Succeeded') {
            this.showSuccess('HA Configuration updated successfully');
            this.loadHostsInformation();
            this.loadSyncStatus();
            // if (this.haConfig.value.useHa) {
            //   this.checkDBLinking();
            // }
          } else {
            this.showError('Failed to update HA Configuration');
          }
          this.isSaving = false;
        },
        error: (error) => {
          console.error("Error updating HA Config:", error);
          
          if (error.status === 422) {
            this.dialog.open(UpdateHostIpComponent, {
              width: '500px',
              data: { 
                currentHostIp: this.haConfig.value.primaryHostIp,
                otherHostIp: this.haConfig.value.secondaryHostIp,
                error: error.error
              }
            });
            this.showError('Host IP validation failed. Please check the configuration.');
          } else {
            this.showError(this.getErrorMessage(error));
          }
          this.isSaving = false;
        }
      });
    } else {
      this.markFormGroupTouched();
      this.showError('Please fill in all required fields correctly');
    }
  }

  loadSyncStatus() {
    this.haService.GetSyncStatus(this.authenticationService.currentUserValue.username)
    .pipe(finalize(() => this.triggerChangeDetection()))
    .subscribe({
      next: (response: any) => {
        
        // Sync status response handling
        if (response && (response.HAEnabled !== undefined || response.haEnabled !== undefined)) {
          this.syncStatus = {
            HAEnabled: response.haEnabled ?? response.HAEnabled ?? false,

            IsDBSyncRunning: response.isDBSyncRunning ?? response.IsDBSyncRunning ?? false,
            DBStatus: response.dbStatus ?? response.DBStatus ?? '',
            DBMessage: response.dbMessage ?? response.DBMessage ?? '',
            DBLastSyncTime: response.dbLastSyncTime ?? response.DBLastSyncTime ?? '',

            IsFSSyncRunning: response.isFSSyncRunning ?? response.IsFSSyncRunning ?? false,
            FSStatus: response.fsStatus ?? response.FSStatus ?? '',
            FSMessage: response.fsMessage ?? response.FSMessage ?? '',
            FSLastSyncTime: response.fsLastSyncTime ?? response.FSLastSyncTime ?? '',

            IsJobsSyncRunning: response.isJobsSyncRunning ?? response.IsJobsSyncRunning ?? false,
            JobsStatus: response.jobsStatus ?? response.JobsStatus ?? '',
            JobsSyncMessage: response.jobsSyncMessage ?? response.JobsSyncMessage ?? '',
            JobsSyncLastSyncTime: response.jobsSyncLastSyncTime ?? response.JobsSyncLastSyncTime ?? '',
          } as SyncStatus;
        } else {
          this.syncStatus = undefined;
        }
      },
      error: (error) => {
        console.error("Error fetching sync status:", error);
        this.showError(`Failed to load sync status: ${this.getErrorMessage(error)}`);
        this.syncStatus = undefined;
      }
    });
  }

  loadLinkedServersInfo(){
    this.haService.GetLinkedServersInfo(this.authenticationService.currentUserValue.username)
    .pipe(finalize(() => this.triggerChangeDetection()))
    .subscribe({
      next: (response: any) => {
        
        // Linked servers response handling
        if (Array.isArray(response)) {
          this.linkedServerInfo = response as LinkedServerInfo[];
        } else if (response) {
          this.linkedServerInfo = [response as LinkedServerInfo];
        } else {
          this.linkedServerInfo = [];
        }
      },
      error: (error) => {
        console.error("Failed to load linked servers:", error);
        this.linkedServerInfo = [];
      }
    });
  }

  loadHostsInformation() {
    this.haService.GetHostsInformation(this.authenticationService.currentUserValue.username)
    .pipe(finalize(() => this.triggerChangeDetection()))
    .subscribe({
      next: (response: any) => {
        // Hosts info response handling (Tuple structure)
        if (response?.item1 && response?.item2) {
          const hostsInfo = response as {item1: HAHostInfo, item2: HAHostInfo};
          this.primaryHost = hostsInfo.item1;
          this.secondaryHost = hostsInfo.item2;
        } else {
          this.primaryHost = undefined;
          this.secondaryHost = undefined;
        }
      },
      error: (error) => {
        console.error("Error fetching hosts information:", error);
        this.showError(`Failed to load hosts information: ${this.getErrorMessage(error)}`);
        this.primaryHost = undefined;
        this.secondaryHost = undefined;
      }
    });
  }

  startDBSync() {
    this.isStartingDBSync = true;
    
    this.haService.startDBSync(this.authenticationService.currentUserValue.username).subscribe({
      next: (response: any) => {
        
        // String response handling
        if (typeof response === 'string' && response.includes('SUCCESSFUL')) {
          this.showSuccess('Database Sync started successfully');
          this.loadSyncStatus();
        } else {
          this.showError('Failed to start Database Sync');
        }
        this.isStartingDBSync = false;
      },
      error: (error) => {
        console.error("Error starting DB Sync:", error);
        this.showError(`Failed to start Database Sync: ${this.getErrorMessage(error)}`);
        this.isStartingDBSync = false;
      }
    });
  }

  stopDBSync() {
    this.isStopingDBSync = true;
    
    this.haService.stopDBSync(this.authenticationService.currentUserValue.username).subscribe({
      next: (response: any) => {
        
        // String response handling
        if (typeof response === 'string' && response.includes('SUCCESSFUL')) {
          this.showSuccess('Database Sync stopped successfully');
          this.loadSyncStatus();
        } else {
          this.showError('Failed to stop Database Sync');
        }
        this.isStopingDBSync = false;
      },
      error: (error) => {
        console.error("Error stopping DB Sync:", error);
        this.showError(`Failed to stop Database Sync: ${this.getErrorMessage(error)}`);
        this.isStopingDBSync = false;
      }
    });
  }

  manualDBSync() {
    this.isManualDBSync = true;
    
    this.haService.manualDBSync(this.authenticationService.currentUserValue.username).subscribe({
      next: (response: any) => {
        
        // SyncResultHA response handling
        if (response?.status) {
          if (response.status.toLowerCase().includes('completed')) {
            if (response.status.toLowerCase().includes('partial')) {
              this.showWarning(`Partial sync completed. Processed: ${response.processedFromCurrent || 0}, Failed: ${response.failedEntries || 0}`);
            } else {
              this.showSuccess('Database Sync completed successfully');
            }
            this.loadSyncStatus();
          } else {
            this.showError('Database Sync failed');
          }
        } else {
          this.showError('Invalid sync response');
        }
        this.isManualDBSync = false;
      },
      error: (error) => {
        console.error("Error during manual DB Sync:", error);
        this.showError(`Failed to execute manual Database Sync: ${this.getErrorMessage(error)}`);
        this.isManualDBSync = false;
      }
    });
  }

  startFSSync() {
    this.isStartingFSSync = true;
    
    this.haService.startFSSync(this.authenticationService.currentUserValue.username).subscribe({
      next: (response: any) => {
        
        // String response handling
        if (typeof response === 'string' && response.includes('SUCCESSFUL')) {
          this.showSuccess('File System Sync started successfully');
          this.loadSyncStatus();
        } else {
          this.showError('Failed to start File System Sync');
        }
        this.isStartingFSSync = false;
      },
      error: (error) => {
        console.error("Error starting FS Sync:", error);
        this.showError(`Failed to start File System Sync: ${this.getErrorMessage(error)}`);
        this.isStartingFSSync = false;
      }
    });
  }

  stopFSSync() {
    this.isStopingFSSync = true;
    
    this.haService.stopFSSync(this.authenticationService.currentUserValue.username).subscribe({
      next: (response: any) => {
        
        // String response handling
        if (typeof response === 'string' && response.includes('SUCCESSFUL')) {
          this.showSuccess('File System Sync stopped successfully');
          this.loadSyncStatus();
        } else {
          this.showError('Failed to stop File System Sync');
        }
        this.isStopingFSSync = false;
      },
      error: (error) => {
        console.error("Error stopping FS Sync:", error);
        this.showError(`Failed to stop File System Sync: ${this.getErrorMessage(error)}`);
        this.isStopingFSSync = false;
      }
    });
  }

  manualFSSync() {
    this.isManualFSSync = true;
    
    this.haService.manualFSSync(this.authenticationService.currentUserValue.username).subscribe({
      next: (response: any) => {
        
        // SyncResultHA response handling
        if (response?.status || response?.Status) {
          this.showSuccess('File System Sync executed successfully');
          this.loadSyncStatus();
        } else {
          this.showError('Failed to execute File System Sync');
        }
        this.isManualFSSync = false;
      },
      error: (error) => {
        console.error("Error during manual FS Sync:", error);
        this.showError(`Failed to execute manual File System Sync: ${this.getErrorMessage(error)}`);
        this.isManualFSSync = false;
      }
    });
  }

  startJobsSync() {
    this.isStartingFSSync = true;
    
    this.haService.startJobsSync(this.authenticationService.currentUserValue.username).subscribe({
      next: (response: any) => {
        
        if (response.status == 'Successful') {
          this.showSuccess('Jobs Sync started successfully');
          this.loadSyncStatus();
        } else {
          this.showError('Failed to start Jobs Sync');
        }
        this.isStartingFSSync = false;
      },
      error: (error) => {
        console.error("Error starting Jobs Sync:", error);
        this.showError(`Failed to start Jobs Sync: ${this.getErrorMessage(error)}`);
        this.isStartingFSSync = false;
      }
    });
  }

  stopJobsSync() {
    this.isStopingFSSync = true;
    
    this.haService.stopJobsSync(this.authenticationService.currentUserValue.username).subscribe({
      next: (response: any) => {
        
        if (response.status == 'Successful') {
          this.showSuccess('Jobs Sync stopped successfully');
          this.loadSyncStatus();
        } else {
          this.showError('Failed to stop Jobs Sync');
        }
        this.isStopingFSSync = false;
      },
      error: (error) => {
        console.error("Error stopping Jobs Sync:", error);
        this.showError(`Failed to stop Jobs Sync: ${this.getErrorMessage(error)}`);
        this.isStopingFSSync = false;
      }
    });
  }

  manualJobsSync() {
    this.isManualJobsSync = true;
    
    this.haService.manualJobsSync(this.authenticationService.currentUserValue.username).subscribe({
      next: (response: any) => {
        
        if (response.status == 'Successful') {
          this.showSuccess('Jobs Sync executed successfully');
          this.loadSyncStatus();
        } else {
          this.showError('Failed to execute Jobs Sync');
        }
        this.isManualJobsSync = false;
      },
      error: (error) => {
        console.error("Error during manual Jobs Sync:", error);
        this.showError(`Failed to execute manual Jobs Sync: ${this.getErrorMessage(error)}`);
        this.isManualJobsSync = false;
      }
    });
  }

  checkDBLinking() {
    this.haService.checkDBLinking(this.authenticationService.currentUserValue.username)
    .pipe(finalize(() => this.triggerChangeDetection()))
    .subscribe({
      next: (response: any) => {
        
        // DB linking response handling
        if (response?.primaryToSecondaryLinking !== undefined && response?.secondaryToPrimaryLinking !== undefined) {
          const linkingStatus = response as {primaryToSecondaryLinking: boolean, secondaryToPrimaryLinking: boolean};
          this.primaryToSecondaryLinking = linkingStatus.primaryToSecondaryLinking;
          this.secondaryToPrimaryLinking = linkingStatus.secondaryToPrimaryLinking;
        } else {
          this.primaryToSecondaryLinking = null;
          this.secondaryToPrimaryLinking = null;
        }
      },
      error: (error) => {
        console.error("Error checking DB Linking:", error);
        this.showError(`Failed to check Database Linking: ${this.getErrorMessage(error)}`);
        this.primaryToSecondaryLinking = null;
        this.secondaryToPrimaryLinking = null;
      }
    });
  }

  configureLinking(linkType: 'primary-to-secondary' | 'secondary-to-primary') {
    if (linkType === 'primary-to-secondary') {
      this.isConfiguringPrimary = true;
    } else if (linkType === 'secondary-to-primary') {
      this.isConfiguringSecondary = true;
    }
    
    this.haService.configureLinking(this.haConfig.value, linkType, this.authenticationService.currentUserValue.username).subscribe({
      next: (response: any) => {
        
        // Linking response handling
        if (response?.Success === true || response?.success === true) {
          const displayType = linkType.split('-').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' ');
          
          this.showSuccess(`Successfully configured ${displayType} linking`);
          this.checkDBLinking();
        } else {
          this.showError('Failed to configure database linking');
        }
        
        this.isConfiguringPrimary = false;
        this.isConfiguringSecondary = false;
      },
      error: (error) => {
        console.error(`Error configuring ${linkType}:`, error);
        this.showError(`Failed to configure linking: ${this.getErrorMessage(error)}`);
        this.isConfiguringPrimary = false;
        this.isConfiguringSecondary = false;
      }
    });
  }

  checkHost(hostType: 'primary' | 'secondary') {
    this.isChecking = true;
    this.checkingHost = hostType;
    
    try {
      this.loadHostsInformation(); 
    } finally {
      setTimeout(() => {
        this.isChecking = false;
        this.checkingHost = '';
      }, 1000);
    }
  }

  // Form and UI Helper Methods
  initForm(conf : HAConfig) {
    this.haConfig = this.form.group({
      primaryHostName: [conf.primaryHostName],
      primaryHostIp: [
        conf.primaryHostIp,
        [
          Validators.required,
          Validators.pattern(/^(?:localhost(?::\d+)?|(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?))$/i)
        ]
      ],
      secondaryHostName: [conf.secondaryHostName],
      secondaryHostIp: [
        conf.secondaryHostIp,
        [
          Validators.required,
          Validators.pattern(/^(?:localhost(?::\d+)?|(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?))$/i)
        ]
      ],
      dbType: [conf.dbType],
      useHa: [conf.useHa]
    });
    this.haEnabled = conf.useHa;
  }

  getFieldError(fieldName: string): string {
    const field = this.haConfig.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) return `${this.getFieldLabel(fieldName)} is required`;
      if (field.errors['pattern']) return `Please enter a valid ${this.getFieldLabel(fieldName).toLowerCase()}`;
      if (field.errors['minlength']) return `${this.getFieldLabel(fieldName)} must be at least ${field.errors['minlength'].requiredLength} characters`;
      if (field.errors['maxlength']) return `${this.getFieldLabel(fieldName)} must not exceed ${field.errors['maxlength'].requiredLength} characters`;
    }
    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      'primaryHostName': 'Primary Host Name',
      'primaryHostIp': 'Primary Host IP',
      'secondaryHostName': 'Secondary Host Name',
      'secondaryHostIp': 'Secondary Host IP',
      'dbType': 'Database Type'
    };
    return labels[fieldName] || fieldName;
  }

  onReset(): void {
    this.initForm(new HAConfig());
    this.snackBar.open('Form reset to default values', 'Close', {
      duration: 2000
    });
  }

  private markFormGroupTouched(): void {
    Object.keys(this.haConfig.controls).forEach(key => {
      const control = this.haConfig.get(key);
      control?.markAsTouched();
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    if (!this.haConfig) return false;
    const field = this.haConfig.get(fieldName);
    return !!(field?.invalid && (field?.dirty || field?.touched));
  }

  toggleHA() {
    this.haEnabled = !this.haEnabled;
    this.haConfig.patchValue({
      useHa: this.haEnabled
    });
  }

  toggleDBType() {
    this.dbtypeEnabled = !this.dbtypeEnabled;
    if (!this.dbtypeEnabled) {
      this.haConfig.patchValue({
        dbType: ''
      });
    }
    else{
      this.haConfig.patchValue({
        dbType: this.databaseTypes[0].value
      });
    }
  }

  formatDate(dateString?: string): string {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).replace(',', '');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  }

  isCurrentHost(host?: any): boolean {
    if (!host || !this.currentHostIp) return false;
    return host.hostIp === this.currentHostIp;
  }

  get primaryHostName(): string {
    return this.primaryHost?.hostName || this.haConfig.get('primaryHostName')?.value || 'Primary Host';
  }

  get primaryHostIp(): string {
    return this.primaryHost?.hostIp || this.haConfig.get('primaryHostIp')?.value || '10.0.0.0';
  }

  get secondaryHostName(): string {
    return this.secondaryHost?.hostName || this.haConfig.get('secondaryHostName')?.value || 'Secondary Host';
  }

  get secondaryHostIp(): string {
    return this.secondaryHost?.hostIp || this.haConfig.get('secondaryHostIp')?.value || '10.0.0.1';
  }

  get shouldShowDataHostInfo(): boolean {
    return this.primaryHost !== undefined && this.secondaryHost !== undefined;
  }

  get shouldShowDataDBLinking(): boolean {
    return this.primaryHost !== undefined && this.secondaryHost !== undefined;
  }

  get shouldShowDataServerInfo(): boolean {
    return this.linkedServerInfo && this.linkedServerInfo.length > 0;
  }

  get shouldShowData(): boolean {
    return (this.primaryHost !== undefined && this.secondaryHost !== undefined) && this.syncStatus !== undefined && this.syncStatus !== null;
  }

  statusActiveOrStandby(host: HAHostInfo,status: string): string {
    return status || 'Unknown';
    if(this.primaryHost && this.secondaryHost){
      if(status == 'Active' && !this.isCurrentHost(host)){
        return 'Standby';
      }
      else{
        return status;
      }
    }else{
      return 'Unknown';
    }
  }

  private triggerChangeDetection(): void {
    setTimeout(() => {}, 0);
  }

  getHostStateIcon(status?: string): string {
    const Status = status?.toLowerCase();
    switch (Status) {
      case 'active':
        return 'bi bi-circle-fill text-success'; // Green circle
      case 'standby':
        return 'bi bi-circle-fill text-warning'; // Orange circle
      case 'inactive':
        return 'bi bi-circle-fill text-danger'; // Red circle
      default:
        return 'bi bi-circle text-muted'; // Gray outline circle
    }
  }

  getHostStateText(status?: string): string {
    const Status = status?.toLowerCase();
    switch (Status) {
      case 'active':
        return 'UP';
      case 'standby':
        return 'STANDBY';
      case 'inactive':
        return 'DOWN';
      default:
        return 'UNKNOWN';
    }
  }

  getHostStateClass(status?: string): string {
    const Status = status?.toLowerCase();
    switch (Status) {
      case 'active':
      case 'standby':
        return 'state-up';
      case 'inactive':
        return 'state-down';
      default:
        return 'state-unknown';
    }
  }

  getHostStatusClasses(status?: string) {
    const Status = status?.toLowerCase();
    return {
      'host-active': Status === 'active',
      'host-standby': Status === 'standby', 
      'host-inactive': Status === 'inactive',
      'host-unknown': !Status || !['active', 'standby', 'inactive'].includes(Status)
    };
  }

  toggleAdvancedOptions() {
    this.showAdvancedOptions = !this.showAdvancedOptions;
  }

  openResetConfirmation() {
    const dialogRef = this.dialog.open(ResetConfirmationDialogComponent, {
      width: '520px',
      maxWidth: '90vw',
      maxHeight: '80vh',
      disableClose: true,
      autoFocus: false, 
      panelClass: 'reset-confirmation-dialog-panel', 
      data: {}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'confirmed') {
        this.executeReset();
      }
    });
  }
  
  executeReset() {
    this.isResetting = true;

    this.haService.resetHASystem(this.authenticationService.currentUserValue.username).subscribe({
      next: (response: any) => {
        this.isResetting = false;
        if (response?.success) {
          this.showSuccess('HA System has been reset successfully. The page will reload.');
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        } else {
          this.showError(`Reset failed: ${response?.message || 'Unknown error'}`);
        }
      },
      error: (error) => {
        this.isResetting = false;
        console.error('Reset error:', error);
        this.showError(`Reset failed: ${this.getErrorMessage(error)}`);
      }
    });
  }

  openMakePrimaryConfirmation() {
    const dialogRef = this.dialog.open(MakePrimaryConfirmationDialogComponent, {
      width: '550px',
      maxWidth: '90vw',
      maxHeight: '80vh',
      disableClose: true,
      autoFocus: false,
      data: {
        currentHostIp: this.currentHostIp,
        primaryHost: this.primaryHost,
        secondaryHost: this.secondaryHost
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'confirmed') {
        this.executeMakePrimary();
      }
    });
  }

  executeMakePrimary() {
    this.isMakingPrimary = true;

    this.haService.makeCurrentHostPrimary(this.authenticationService.currentUserValue.username).subscribe({
      next: (response: any) => {
        this.isMakingPrimary = false;
        if (response === true || response?.success === true) {
          this.showSuccess('Current host has been made primary successfully. Refreshing host information...');
          // Refresh the host information to reflect changes
          setTimeout(() => {
            this.loadHostsInformation();
            this.loadSyncStatus();
          }, 1000);
        } else {
          this.showError(`Failed to make current host primary: ${response?.message || 'Unknown error'}`);
        }
      },
      error: (error) => {
        this.isMakingPrimary = false;
        console.error('Make Primary error:', error);
        this.showError(`Failed to make current host primary: ${this.getErrorMessage(error)}`);
      }
    });
  }

}
